import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PersonalInfoService } from '../../../shared/services/personal-info.service';
import config from '../../../../assets/config.json';

@Component({
  selector: 'app-pin-entry',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './pin-entry.component.html',
  styleUrl: './pin-entry.component.scss',
})
export class PinEntryComponent implements OnInit {
  pinEntryForm!: FormGroup;
  isSubmitted: boolean = false;
  errMsg: String = '';
  customError: boolean = false;
  customSuccess: boolean = false;
  isPinWasResent: boolean = false;
  maskedPhoneNo: any = undefined;
  MFALoginKeyName: string = config.MFALoginKeyName;

  constructor(
    private _router: Router,
    private _personalInfo: PersonalInfoService,
  ) {}

  ngOnInit(): void {
    //pin form initialization
    this.pinEntryForm = new FormGroup({
      pin: new FormControl('', [Validators.required, Validators.pattern('^[0-9]+$')]),
    });
    this.maskedPhoneNo = history?.state?.pinEntry?.res?.passCode?.maskedPhoneNo;
    this.pinEntryForm.get('pin')?.reset('');
  }

  // convenience getter for easy access to form fields
  get form() {
    return this.pinEntryForm.controls;
  }

  reSendCode() {
    this.isPinWasResent = true;
    const payload: any = {
      requestingSystem: 'FinPro',
      securityCode: '',
      resendCode: true,
      browserName: this.getBrowserName(),
    };
    this._personalInfo.ValidatePin(payload).subscribe((response) => {
      if (response.action === 'PassCode' && response.passCode.isPinWasResent) {
        this.customSuccess = true;
        this.customError = false;
        setTimeout(() => {
          this.customSuccess = false;
        }, 2000);
        this.pinEntryForm.get('pin')?.reset('');
      } else if (response.action === 'CallUs') {
        this._router.navigate(['/user-profile/call-us']);
      }
    });
  }

  // Submit the form
  onSubmit() {
    this.customError = false;
    this.customSuccess = false;
    this.isSubmitted = true;
    this.isPinWasResent = false;
    if (this.pinEntryForm.valid) {
      const pin = this.pinEntryForm.value.pin;
      const payload: any = {
        requestingSystem: 'FinPro',
        securityCode: this.isPinWasResent ? '' : pin,
        resendCode: this.isPinWasResent ? true : false, // if the user click on resend button it needs to be true and securityCode should be empty
        browserName: this.getBrowserName(),
      };
      this.disableEnableSubmitButton(true); // Disable the button
      this._personalInfo.ValidatePin(payload).subscribe(
        (response) => {
          if (response.action === 'Success' && response.skipMFA) {
            this.saveMFAToken(response?.a9b7xk_0q); // Save the token in a cookie
            this._router.navigate(['/advisor']);
          } else if (response.action === 'Success' && !response.skipMFA) {
            this._router.navigate(['/advisor/mfa']); //need to change the right path
          } else if (response.action === 'SecurityQuestion') {
            const securityQA = { qA: response };
            this._router.navigate(['/user-profile/security-question'], { state: { securityQA } });
          } else if (response.action === 'PassCode' && response.passCode.isInvalidPin) {
            this.pinEntryForm.get('pin')?.reset('');
            this.customError = true;
            this.customSuccess = false;
            setTimeout(() => {
              this.customError = false;
            }, 2000);
          } else {
            this._router.navigate(['/user-profile/call-us']);
            this.errMsg = 'Invalid email address';
          }
        },
        () => {
          this.disableEnableSubmitButton(false); // Enable the button after response
        },
      );
    } else {
      this.customError = true;
      this.customSuccess = false;
      setTimeout(() => {
        this.customError = false;
      }, 2000);
      setTimeout(() => {
        const inputElement = document.querySelector('.ng-pristine.ng-invalid input');
        (inputElement as HTMLElement).focus();
      }, 100);
      this.errMsg = 'Please enter a valid PIN.';
    }
  }

  disableEnableSubmitButton(isEnable: boolean) {
    const submitButton = document.getElementById('validate-button') as HTMLButtonElement;
    if (isEnable && submitButton) {
      submitButton.disabled = true; // Disable the button
      submitButton.style.cursor = 'not-allowed'; // Change cursor to indicate disabled state
      submitButton.style.opacity = '0.6'; // Reduce opacity to visually indicate disabled state
      submitButton.style.pointerEvents = 'none'; // Disable hover effect
    } else {
      // Enable the button after response
      submitButton.disabled = false; // Enable the button
      submitButton.style.cursor = 'pointer'; // Change cursor back to pointer
      submitButton.style.pointerEvents = 'auto'; // Re-enable hover effect
      submitButton.style.opacity = '1'; // Reduce opacity to visually indicate disabled state
    }
  }

  saveMFAToken(token: string): void {
    const date = new Date(Date.now() + Number(config.idleAppMfaTimeOut)); // Set expiration date using config.idleAppMfaTimeOut
    const expires = 'expires=' + date.toUTCString(); // Convert the date to UTC string
    const encodedToken = btoa(token); // Base64 encode the token to ensure it's safely stored
    // Set the cookie with the 'Secure' and 'Path' attributes
    document.cookie = `${this.MFALoginKeyName}=${encodedToken}; ${expires}; path=/; Secure;`;
  }

  /**
   * Returns the browser name based on the user agent string.
   */
  getBrowserName(): string {
    const userAgent = navigator.userAgent;
    if (/chrome|crios|crmo/i.test(userAgent) && !/edge|edg|opr|opera/i.test(userAgent)) {
      return 'Chrome';
    }
    if (/firefox|fxios/i.test(userAgent)) {
      return 'Firefox';
    }
    if (/safari/i.test(userAgent) && !/chrome|crios|crmo|android/i.test(userAgent)) {
      return 'Safari';
    }
    if (/edg/i.test(userAgent)) {
      return 'Edge';
    }
    if (/opr|opera/i.test(userAgent)) {
      return 'Opera';
    }
    if (/msie|trident/i.test(userAgent)) {
      return 'Internet Explorer';
    }
    return 'Unknown';
  }
}
